import type {WorkflowRun} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import {useQueryClient, type QueryKey} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {memo, useCallback, useEffect, useMemo, useRef} from 'react'

const REPOSITORY_BATCH_DELAY_MS = 10_000
const WORKFLOW_RUN_BATCH_DELAY_MS = 30_000
const REFRESH_COALESCE_DELAY_MS = 50

interface LiveUpdateBatch {
  interval: ReturnType<typeof setInterval>
  pendingIds: Set<string>
}

interface LiveUpdateSubscription {
  id: string
  batchDelayMs: number
}

interface WorkflowRunsLiveUpdatesProps {
  aliveChannel?: string
  enabled?: boolean
  /** Lengthens each channel's batch window to at least this long. */
  minBatchDelayMs?: number
  workflowRuns: WorkflowRun[]
  queryKey: QueryKey
}

export function WorkflowRunsLiveUpdates({
  aliveChannel,
  enabled = true,
  minBatchDelayMs = 0,
  workflowRuns,
  queryKey,
}: WorkflowRunsLiveUpdatesProps) {
  const queryClient = useQueryClient()
  const batchesRef = useRef(new Map<number, LiveUpdateBatch>())
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const queryKeyRef = useRef(queryKey)
  const subscriptions = useMemo(() => {
    const result = new Map<string, LiveUpdateSubscription>()
    if (!enabled) return result
    for (const run of workflowRuns) {
      if (run.aliveChannel) {
        result.set(run.aliveChannel, {
          id: `run:${run.id}`,
          batchDelayMs: Math.max(WORKFLOW_RUN_BATCH_DELAY_MS, minBatchDelayMs),
        })
      }
    }
    if (aliveChannel) {
      result.set(aliveChannel, {id: 'repository', batchDelayMs: Math.max(REPOSITORY_BATCH_DELAY_MS, minBatchDelayMs)})
    }
    return result
  }, [aliveChannel, enabled, minBatchDelayMs, workflowRuns])
  const activeIds = useMemo(
    () => new Map([...subscriptions.values()].map(({id, batchDelayMs}) => [id, batchDelayMs])),
    [subscriptions],
  )
  const activeIdsRef = useRef(activeIds)

  useEffect(() => {
    queryKeyRef.current = queryKey
    activeIdsRef.current = activeIds
    for (const entry of batchesRef.current) {
      const [batchDelayMs, {pendingIds}] = entry
      for (const id of pendingIds) {
        if (activeIds.get(id) !== batchDelayMs) pendingIds.delete(id)
      }
    }
  }, [activeIds, queryKey])

  const scheduleInvalidation = useCallback(
    (id: string, batchDelayMs: number) => {
      if (!enabled) return

      let batch = batchesRef.current.get(batchDelayMs)
      if (!batch) {
        const pendingIds = new Set<string>()
        // Rails uses fixed data-batched windows, without adding the event's replication wait.
        const interval = setInterval(() => {
          const hasCurrentUpdates = [...pendingIds].some(
            pendingId => activeIdsRef.current.get(pendingId) === batchDelayMs,
          )
          pendingIds.clear()
          if (hasCurrentUpdates && refreshTimeoutRef.current === undefined) {
            // Share ready batches without restarting the wait or suppressing updates during the fetch.
            refreshTimeoutRef.current = setTimeout(() => {
              refreshTimeoutRef.current = undefined
              void queryClient.invalidateQueries({queryKey: queryKeyRef.current, exact: true})
            }, REFRESH_COALESCE_DELAY_MS)
          }
        }, batchDelayMs)
        batch = {interval, pendingIds}
        batchesRef.current.set(batchDelayMs, batch)
      }
      batch.pendingIds.add(id)
    },
    [enabled, queryClient],
  )

  useEffect(
    () => () => {
      clearTimeout(refreshTimeoutRef.current)
      refreshTimeoutRef.current = undefined
      for (const batch of batchesRef.current.values()) clearInterval(batch.interval)
      batchesRef.current.clear()
    },
    [enabled, minBatchDelayMs],
  )

  return [...subscriptions].map(([channel, {id, batchDelayMs}]) => (
    <WorkflowRunsAliveSubscription
      key={id}
      subscriptionId={id}
      channel={channel}
      batchDelayMs={batchDelayMs}
      onNotification={scheduleInvalidation}
    />
  ))
}

const WorkflowRunsAliveSubscription = memo(function WorkflowRunsAliveSubscriptionComponent({
  subscriptionId,
  channel,
  batchDelayMs,
  onNotification,
}: {
  subscriptionId: string
  channel: string
  batchDelayMs: number
  onNotification: (subscriptionId: string, batchDelayMs: number) => void
}) {
  const handleNotification = useCallback(
    () => onNotification(subscriptionId, batchDelayMs),
    [batchDelayMs, onNotification, subscriptionId],
  )
  useAlive(channel, handleNotification)
  return null
})
